import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch, apiJson, resolvePublicStorageUrl } from '../apiClient';
import { buildWhatsAppHref, tzPhoneToE164Digits } from '../utils/supportContact';
import { useAuth } from '../AuthContext';
import { SalesHistoryContext } from '../SalesHistoryContext';
import './BrickFactoryPage.css';
import './FactoryBrickPreOrdersPage.css';

const STATUS_LABELS = {
  awaiting_production: 'Awaiting production',
  ready_to_deliver: 'Ready to deliver',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatPcs(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-TZ');
}

function formatTzs(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-TZ', { maximumFractionDigits: 0 });
}

function payPercent(paid, total) {
  const t = Number(total);
  if (!t) return 0;
  return Math.round((Number(paid) / t) * 100);
}

export default function FactoryBrickPreOrdersPage({ onNavigate }) {
  const { user } = useAuth();
  const { refreshSales } = useContext(SalesHistoryContext);
  const [types, setTypes] = useState([]);
  const [preOrders, setPreOrders] = useState([]);
  const [filterTypeId, setFilterTypeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [evidenceOrder, setEvidenceOrder] = useState(null);
  const [noticeOrder, setNoticeOrder] = useState(null);
  const [noticeText, setNoticeText] = useState('');
  const [payOrder, setPayOrder] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payRef, setPayRef] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [evidencePreviewKind, setEvidencePreviewKind] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const [form, setForm] = useState({
    brickTypeId: '',
    customerName: '',
    customerPhone: '',
    destination: '',
    quantity: '',
    unitPriceTzs: '',
    amountPaidTzs: '',
    paymentMethod: 'cash',
    paymentReference: '',
    smsMessage: '',
    notes: '',
  });

  const activeTypes = useMemo(() => types.filter((t) => t.isActive !== false), [types]);

  const activeQueue = useMemo(
    () =>
      preOrders.filter((o) => o.status === 'awaiting_production' || o.status === 'ready_to_deliver'),
    [preOrders]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, q] = await Promise.all([
        apiJson('/brick/types'),
        apiJson(
          `/brick/pre-orders${filterTypeId ? `?brickTypeId=${encodeURIComponent(filterTypeId)}` : ''}`
        ),
      ]);
      setTypes(t.types || []);
      setPreOrders(q.preOrders || []);
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Failed to load pre-orders.' });
    } finally {
      setLoading(false);
    }
  }, [filterTypeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!form.brickTypeId && activeTypes.length) {
      const t = activeTypes[0];
      setForm((f) => ({
        ...f,
        brickTypeId: String(t.id),
        unitPriceTzs: t.defaultSellingPrice != null ? String(t.defaultSellingPrice) : '',
      }));
    }
  }, [activeTypes, form.brickTypeId]);

  const lineTotal = useMemo(() => {
    const q = Number(form.quantity) || 0;
    const u = Number(form.unitPriceTzs) || 0;
    return Math.round(q * u);
  }, [form.quantity, form.unitPriceTzs]);

  useEffect(() => {
    return () => {
      if (evidencePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(evidencePreview);
      }
    };
  }, [evidencePreview]);

  const resetFormEvidence = () => {
    if (evidencePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(evidencePreview);
    }
    setEvidenceFile(null);
    setEvidencePreview(null);
    setEvidencePreviewKind(null);
    setFileKey((k) => k + 1);
  };

  const handleEvidenceChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (evidencePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(evidencePreview);
    }
    setEvidenceFile(file);
    if (!file) {
      setEvidencePreview(null);
      setEvidencePreviewKind(null);
      return;
    }
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (isPdf) {
      setEvidencePreviewKind('pdf');
      setEvidencePreview(URL.createObjectURL(file));
      return;
    }
    if (!file.type.startsWith('image/')) {
      setEvidencePreview(null);
      setEvidencePreviewKind(null);
      return;
    }
    setEvidencePreviewKind('image');
    const reader = new FileReader();
    reader.onloadend = () => setEvidencePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    const total = lineTotal;
    const paid = Number(form.amountPaidTzs) || 0;
    if (!total || paid <= 0) {
      setMsg({ type: 'error', text: 'Enter quantity, price, and amount paid.' });
      setBusy(false);
      return;
    }
    if (paid > total) {
      setMsg({ type: 'error', text: 'Amount paid cannot exceed total.' });
      setBusy(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append('brickTypeId', form.brickTypeId);
      fd.append('customerName', form.customerName.trim());
      if (form.customerPhone.trim()) fd.append('customerPhone', form.customerPhone.trim());
      if (form.destination.trim()) fd.append('destination', form.destination.trim());
      fd.append('quantity', String(form.quantity));
      fd.append('unitPriceTzs', String(form.unitPriceTzs));
      fd.append('amountPaidTzs', String(paid));
      fd.append('paymentMethod', form.paymentMethod);
      if (form.paymentReference.trim()) fd.append('paymentReference', form.paymentReference.trim());
      if (form.smsMessage.trim()) fd.append('smsMessage', form.smsMessage.trim());
      if (form.notes.trim()) fd.append('notes', form.notes.trim());
      if (evidenceFile) fd.append('evidence', evidenceFile);

      const res = await apiFetch('/brick/pre-orders', { method: 'POST', body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Could not save pre-order.');

      setForm((f) => ({
        ...f,
        customerName: '',
        customerPhone: '',
        destination: '',
        quantity: '',
        amountPaidTzs: '',
        paymentReference: '',
        smsMessage: '',
        notes: '',
      }));
      resetFormEvidence();
      await load();
      setMsg({
        type: 'success',
        text: `Pre-order #${body.preOrder?.queuePosition ?? '—'} saved (${payPercent(paid, total)}% paid).`,
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const runAction = async (id, action, extra = {}) => {
    setBusy(true);
    setMsg(null);
    try {
      const isPayment = action === 'add_payment';
      let res;
      if (isPayment && extra.formData) {
        res = await apiFetch(`/brick/pre-orders/${id}`, { method: 'PATCH', body: extra.formData });
      } else {
        res = await apiFetch(`/brick/pre-orders/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ action, ...extra }),
        });
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Action failed.');
      await load();
      setMsg({ type: 'success', text: extra.successText || 'Updated.' });
      return body;
    } catch (err) {
      setMsg({ type: 'error', text: err.message || 'Action failed.' });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!payOrder) return;
    const amt = Number(payAmount);
    if (!amt || amt <= 0) return;
    const fd = new FormData();
    fd.append('action', 'add_payment');
    fd.append('amountTzs', String(amt));
    if (payRef.trim()) fd.append('paymentReference', payRef.trim());
    const body = await runAction(payOrder.id, 'add_payment', {
      formData: fd,
      successText: 'Payment recorded.',
    });
    if (body) {
      setPayOrder(null);
      setPayAmount('');
      setPayRef('');
    }
  };

  const handleNotice = async (e) => {
    e.preventDefault();
    if (!noticeOrder || !noticeText.trim()) return;
    const body = await runAction(noticeOrder.id, 'record_notice', {
      message: noticeText.trim(),
      successText: 'Customer notice recorded.',
    });
    if (body) {
      setNoticeOrder(null);
      setNoticeText('');
    }
  };

  const openNotice = (order) => {
    setNoticeOrder(order);
    setNoticeText(
      order.lastNoticeMessage ||
        `Habari ${order.customerName}, matofali yako (${order.brickTypeName}, ${formatPcs(order.quantity)} pcs) bado yanatengenezwa. Tutakujulisha tarehe ya utoaji. Asante kwa kusubiri.`
    );
  };

  const sendNoticeWhatsApp = (order, message) => {
    const phone = tzPhoneToE164Digits(order?.customerPhone || '');
    if (!phone || phone.length < 11) {
      setMsg({
        type: 'error',
        text: 'Weka namba ya simu ya mteja (07… au 255…) kwenye agizo hili kwanza.',
      });
      return;
    }
    const text = String(message || '').trim();
    if (!text) {
      setMsg({ type: 'error', text: 'Andika ujumbe kwanza.' });
      return;
    }
    const href = buildWhatsAppHref(phone, text);
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const renderStatus = (status) => {
    const label = STATUS_LABELS[status] || status;
    const cls =
      status === 'delivered'
        ? 'preorder-status preorder-status--done'
        : status === 'ready_to_deliver'
          ? 'preorder-status preorder-status--ready'
          : status === 'cancelled'
            ? 'preorder-status preorder-status--cancel'
            : 'preorder-status preorder-status--wait';
    return <span className={cls}>{label}</span>;
  };

  return (
    <section className="brick-page factory-preorder-page page-bleed">
      <header className="factory-preorder__hero">
        <div>
          <h1 className="factory-preorder__title">Pre-orders (agizo la mteja)</h1>
          <p className="factory-preorder__sub">
            Mteja analipa mapema (100% au deposit) — foleni ni kwa kila aina ya tofali (FIFO). Weka{' '}
            <strong>SMS ya muamala</strong> (M-Pesa n.k.) na screenshot hapa. Utoaji —{' '}
            <button type="button" className="brick-link" onClick={() => onNavigate?.('brick-deliveries')}>
              Deliveries
            </button>
            .
          </p>
        </div>
        <div className="factory-preorder__stats">
          <span>
            <strong>{activeQueue.length}</strong> in queue
          </span>
        </div>
      </header>

      {msg ? <div className={`brick-msg brick-msg--${msg.type}`}>{msg.text}</div> : null}

      <section className="brick-card factory-preorder-form-card">
        <h2 className="factory-preorder__section-title">New pre-order</h2>
        <form className="brick-form brick-form--wide" onSubmit={handleSubmit}>
          <div className="brick-form__row">
            <label>
              Brick type *
              <select
                required
                value={form.brickTypeId}
                onChange={(e) => {
                  const id = e.target.value;
                  const t = activeTypes.find((x) => String(x.id) === id);
                  setForm((f) => ({
                    ...f,
                    brickTypeId: id,
                    unitPriceTzs:
                      t?.defaultSellingPrice != null ? String(t.defaultSellingPrice) : f.unitPriceTzs,
                  }));
                }}
              >
                {activeTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Quantity (pcs) *
              <input
                type="number"
                min="1"
                required
                value={form.quantity}
                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            </label>
          </div>
          <div className="brick-form__row">
            <label>
              Unit price (TZS) *
              <input
                type="number"
                min="0"
                step="1"
                required
                value={form.unitPriceTzs}
                onChange={(e) => setForm((f) => ({ ...f, unitPriceTzs: e.target.value }))}
              />
            </label>
            <label>
              Order total
              <input type="text" readOnly value={lineTotal ? `TZS ${formatTzs(lineTotal)}` : '—'} />
            </label>
          </div>
          <div className="brick-form__row">
            <label>
              Customer name *
              <input
                required
                value={form.customerName}
                onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
              />
            </label>
            <label>
              Phone
              <input
                value={form.customerPhone}
                onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
              />
            </label>
          </div>
          <label>
            Delivery site / destination
            <input
              value={form.destination}
              onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
              placeholder="Eneo la ujenzi"
            />
          </label>
          <div className="brick-form__row">
            <label>
              Amount paid now (TZS) *
              <input
                type="number"
                min="1"
                required
                value={form.amountPaidTzs}
                onChange={(e) => setForm((f) => ({ ...f, amountPaidTzs: e.target.value }))}
              />
              {lineTotal > 0 && form.amountPaidTzs ? (
                <span className="factory-preorder-hint">
                  {payPercent(form.amountPaidTzs, lineTotal)}% · balance TZS{' '}
                  {formatTzs(lineTotal - (Number(form.amountPaidTzs) || 0))}
                </span>
              ) : null}
            </label>
            <label>
              Payment method
              <select
                value={form.paymentMethod}
                onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
              >
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="lipa">Lipa number</option>
                <option value="bank">Bank</option>
              </select>
            </label>
          </div>
          <div className="brick-form__row">
            <label>
              Payment reference
              <input
                value={form.paymentReference}
                onChange={(e) => setForm((f) => ({ ...f, paymentReference: e.target.value }))}
                placeholder="M-Pesa code / receipt no."
              />
            </label>
          </div>
          <label>
            SMS ya muamala (malipo)
            <textarea
              rows={3}
              value={form.smsMessage}
              onChange={(e) => setForm((f) => ({ ...f, smsMessage: e.target.value }))}
              placeholder="Bandika SMS iliyotoka M-Pesa / bank / Lipa — mfano: CONFIRMED, Tsh ..., ref, tarehe"
            />
            <span className="factory-preorder-hint">
              Ni SMS halisi ya malipo uliyopokea simuni, si maelezo ya mkataba.
            </span>
          </label>
          <div className="factory-preorder-file-wrap">
            <span className="factory-preorder-file-label">Screenshot ya muamala (optional)</span>
            <input
              key={fileKey}
              type="file"
              accept="image/*,application/pdf"
              className="factory-preorder-file-input"
              id="preorder-evidence"
              onChange={handleEvidenceChange}
            />
            <label htmlFor="preorder-evidence" className="factory-preorder-file-btn">
              Chagua picha / PDF
            </label>
            {evidenceFile?.name ? (
              <span className="factory-preorder-file-name">
                {evidenceFile.name}
                {evidencePreviewKind === 'pdf' ? ' (PDF)' : evidencePreviewKind === 'image' ? ' (picha)' : ''}
              </span>
            ) : null}
            {evidencePreview && evidencePreviewKind === 'image' ? (
              <div className="factory-preorder-media-preview">
                <img src={evidencePreview} alt="Screenshot ya muamala" />
              </div>
            ) : null}
            {evidencePreview && evidencePreviewKind === 'pdf' ? (
              <div className="factory-preorder-media-preview factory-preorder-media-preview--pdf">
                <embed src={evidencePreview} type="application/pdf" title="PDF ya muamala" />
                <p className="factory-preorder-hint">Hakiki PDF hapo juu kabla ya kuhifadhi.</p>
              </div>
            ) : null}
          </div>
          <button type="submit" className="brick-btn brick-btn--secondary" disabled={busy || loading}>
            Save pre-order
          </button>
        </form>
      </section>

      <section className="brick-card brick-card--grow">
        <div className="factory-preorder__list-head">
          <h2 className="factory-preorder__section-title">Queue (FIFO per brick type)</h2>
          <label className="factory-preorder-filter">
            Filter type
            <select value={filterTypeId} onChange={(e) => setFilterTypeId(e.target.value)}>
              <option value="">All types</option>
              {activeTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {loading ? <p className="brick-loading">Loading…</p> : null}
        {!loading && !preOrders.length ? (
          <p className="brick-empty">Hakuna pre-orders bado.</p>
        ) : null}
        {preOrders.length ? (
          <div className="brick-table-wrap">
            <table className="brick-table factory-preorder-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Brick type</th>
                  <th>Qty</th>
                  <th>Paid / Total</th>
                  <th>Status</th>
                  <th>Recorded by</th>
                  <th>Evidence</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {preOrders.map((o) => (
                  <tr
                    key={o.id}
                    className={
                      o.queuePosition === 1 && o.status === 'awaiting_production'
                        ? 'factory-preorder-row--first'
                        : ''
                    }
                  >
                    <td>
                      {o.queuePosition ? (
                        <strong className="factory-preorder-queue">#{o.queuePosition}</strong>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{o.createdAt?.slice(0, 10) ?? '—'}</td>
                    <td>
                      <strong>{o.customerName}</strong>
                      {o.customerPhone ? <div className="factory-preorder-sub">{o.customerPhone}</div> : null}
                    </td>
                    <td>{o.brickTypeName}</td>
                    <td>{formatPcs(o.quantity)}</td>
                    <td>
                      TZS {formatTzs(o.amountPaidTzs)}
                      <div className="factory-preorder-sub">
                        of {formatTzs(o.totalAmountTzs)}
                        {!o.isFullyPaid ? ` · bal ${formatTzs(o.balanceAmountTzs)}` : ' · paid'}
                      </div>
                    </td>
                    <td>
                      {renderStatus(o.status)}
                      {o.status === 'delivered' ? (
                        <div className="factory-preorder-sub">
                          {o.saleId ? (
                            <button
                              type="button"
                              className="factory-preorder-link-btn"
                              onClick={() => onNavigate?.('pos-sale-history')}
                            >
                              Sale #{o.saleId}
                            </button>
                          ) : null}
                          {o.deliveryId ? (
                            <button
                              type="button"
                              className="factory-preorder-link-btn"
                              onClick={() => onNavigate?.('brick-deliveries')}
                            >
                              Delivery #{o.deliveryId}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                    <td>{o.recordedBy || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="factory-preorder-link-btn"
                        onClick={() => setEvidenceOrder(o)}
                      >
                        View
                      </button>
                    </td>
                    <td>
                      {o.status === 'awaiting_production' || o.status === 'ready_to_deliver' ? (
                        <div className="factory-preorder-actions">
                          {!o.isFullyPaid ? (
                            <button
                              type="button"
                              className="factory-preorder-btn factory-preorder-btn--pay"
                              disabled={busy}
                              onClick={() => {
                                setPayOrder(o);
                                setPayAmount(String(o.balanceAmountTzs || ''));
                              }}
                            >
                              + Pay
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="factory-preorder-btn"
                            disabled={busy}
                            onClick={() => openNotice(o)}
                          >
                            Notice
                          </button>
                          {o.status === 'awaiting_production' ? (
                            <button
                              type="button"
                              className="factory-preorder-btn"
                              disabled={busy}
                              onClick={() =>
                                void runAction(o.id, 'mark_ready', { successText: 'Marked ready to deliver.' })
                              }
                            >
                              Ready
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="factory-preorder-btn factory-preorder-btn--fulfill"
                            disabled={busy}
                            onClick={() =>
                              void runAction(o.id, 'fulfill', {
                                deliveryDate: todayKey(),
                                successText: 'Pre-order fulfilled — sale recorded, stock deducted, delivery created.',
                              }).then(async (b) => {
                                if (b?.sale) {
                                  await refreshSales?.();
                                }
                                if (b?.delivery && onNavigate) onNavigate('brick-deliveries');
                              })
                            }
                          >
                            Fulfill
                          </button>
                          <button
                            type="button"
                            className="factory-preorder-btn factory-preorder-btn--cancel"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm('Cancel this pre-order?')) {
                                void runAction(o.id, 'cancel', { successText: 'Pre-order cancelled.' });
                              }
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {evidenceOrder ? (() => {
        const evidenceSrc = resolvePublicStorageUrl(evidenceOrder.evidenceUrl);
        const evidenceIsPdf = evidenceSrc && /\.pdf(\?|$)/i.test(evidenceSrc);
        return (
        <div className="factory-preorder-modal-backdrop" role="presentation" onClick={() => setEvidenceOrder(null)}>
          <div className="factory-preorder-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Pre-order #{evidenceOrder.id} — {evidenceOrder.customerName}</h3>
            <p>
              <strong>Paid:</strong> TZS {formatTzs(evidenceOrder.amountPaidTzs)} /{' '}
              {formatTzs(evidenceOrder.totalAmountTzs)}
              {evidenceOrder.paymentReference ? ` · Ref: ${evidenceOrder.paymentReference}` : ''}
            </p>
            {evidenceOrder.smsMessage ? (
              <>
                <p className="factory-preorder-evidence-label">SMS ya muamala</p>
                <pre className="factory-preorder-sms">{evidenceOrder.smsMessage}</pre>
              </>
            ) : (
              <p className="brick-empty">Hakuna SMS ya muamala iliyorekodiwa.</p>
            )}
            {evidenceSrc ? (
              evidenceIsPdf ? (
                <div className="factory-preorder-modal-pdf">
                  <embed src={evidenceSrc} type="application/pdf" title="Screenshot ya muamala" />
                  <a href={evidenceSrc} target="_blank" rel="noopener noreferrer" className="brick-link">
                    Fungua PDF kwenye tab mpya
                  </a>
                </div>
              ) : (
                <a href={evidenceSrc} target="_blank" rel="noopener noreferrer" className="factory-preorder-evidence-link">
                  <img src={evidenceSrc} alt="Screenshot ya muamala" className="factory-preorder-evidence-img" />
                </a>
              )
            ) : (
              <p className="brick-empty">Hakuna screenshot iliyopakiwa.</p>
            )}
            {evidenceOrder.lastNoticeMessage ? (
              <p className="factory-preorder-notice-log">
                <strong>Last notice ({evidenceOrder.lastNoticeAt?.slice(0, 10) ?? ''}):</strong>{' '}
                {evidenceOrder.lastNoticeMessage}
              </p>
            ) : null}
            <div className="factory-preorder-modal-actions">
              {evidenceSrc ? (
                <a
                  href={evidenceSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brick-btn brick-btn--secondary brick-btn--sm"
                >
                  Fungua screenshot
                </a>
              ) : null}
              <button type="button" className="brick-btn brick-btn--secondary brick-btn--sm" onClick={() => setEvidenceOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })() : null}

      {noticeOrder ? (
        <div className="factory-preorder-modal-backdrop" role="presentation" onClick={() => setNoticeOrder(null)}>
          <div className="factory-preorder-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Customer notice — {noticeOrder.customerName}</h3>
            {noticeOrder.customerPhone ? (
              <p className="factory-preorder-notice-phone">
                WhatsApp / simu: <strong>{noticeOrder.customerPhone}</strong>
              </p>
            ) : (
              <p className="factory-preorder-notice-phone factory-preorder-notice-phone--warn">
                Hakuna namba ya simu — ongeza kwenye agizo ili kutuma WhatsApp.
              </p>
            )}
            <p className="factory-preorder-hint">
              Andika ujumbe kumjulisha kuhusu kuchelewa au hali ya agizo. Tumia WhatsApp kumtumia moja kwa moja,
              kisha hifadhi rekodi hapa.
            </p>
            <form onSubmit={handleNotice}>
              <textarea
                rows={4}
                required
                value={noticeText}
                onChange={(e) => setNoticeText(e.target.value)}
                className="factory-preorder-notice-input"
                placeholder="Ujumbe kwa mteja…"
              />
              <div className="factory-preorder-modal-actions">
                <button
                  type="button"
                  className="factory-preorder-btn-whatsapp"
                  disabled={!noticeText.trim() || !noticeOrder.customerPhone}
                  onClick={() => sendNoticeWhatsApp(noticeOrder, noticeText)}
                >
                  Tuma WhatsApp
                </button>
                <button type="submit" className="brick-btn brick-btn--secondary" disabled={busy}>
                  Hifadhi notice
                </button>
                <button type="button" className="brick-btn" onClick={() => setNoticeOrder(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {payOrder ? (
        <div className="factory-preorder-modal-backdrop" role="presentation" onClick={() => setPayOrder(null)}>
          <div className="factory-preorder-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Add payment — {payOrder.customerName}</h3>
            <p>
              Balance: TZS {formatTzs(payOrder.balanceAmountTzs)}
            </p>
            <form onSubmit={handleAddPayment}>
              <label>
                Amount (TZS)
                <input
                  type="number"
                  min="1"
                  required
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </label>
              <label>
                Reference
                <input value={payRef} onChange={(e) => setPayRef(e.target.value)} />
              </label>
              <div className="factory-preorder-modal-actions">
                <button type="submit" className="brick-btn brick-btn--secondary" disabled={busy}>
                  Record payment
                </button>
                <button type="button" className="brick-btn" onClick={() => setPayOrder(null)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
